import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { parseIncompletePhoneNumber } from "libphonenumber-js";
import { isEmpty, isObject } from "lodash";
import React, { useState } from "react";
import TagManager from "react-gtm-module";
import { Path, useController, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";
import { router } from "router";
import * as Yup from "yup";
import { useShallow } from "zustand/react/shallow";

import { ConsentCheckboxValidation } from "@krea/customer-features/form-validation/ConsentCheckboxValidation";
import { EmailValidation } from "@krea/customer-features/form-validation/EmailValidation";
import { OrganisationValidation } from "@krea/customer-features/form-validation/OrganisationValidation";
import { MobilePhoneNumberValidation } from "@krea/customer-features/form-validation/PhoneNumberValidation";
import { RequestedLoanAmountValidation } from "@krea/customer-features/form-validation/RequestedLoanAmountValidation";
import { useMobile } from "@krea/customer-features/hooks";
import Check from "@krea/customer-features/images/svg/check.svg";
import {
  ICustomExceptionError,
  IErrorResponse,
} from "@krea/customer-features/models/commonInterfaces";
import { getUtmDataJSONString } from "@krea/customer-features/services/formService";
import { settings } from "@krea/customer-features/settings";
import Button from "@krea/customer-features/shared-components/button";
import AppFormPromotion from "@krea/customer-features/shared-components/loan-application-form/AppFormPromotion";
import AmountSlider from "@krea/customer-features/shared-components/loan-application-form/fields/amount-slider";
import EmailInput from "@krea/customer-features/shared-components/loan-application-form/fields/email";
import OrganisationInput from "@krea/customer-features/shared-components/loan-application-form/fields/organisation-number";
import PhoneInput from "@krea/customer-features/shared-components/loan-application-form/fields/phone";
import LoanApplicationFormLayout from "@krea/customer-features/shared-components/loan-application-form-layout";
import { Text } from "@krea/customer-features/shared-components/text";
import {
  IPreliminaryLoanAmountData,
  useLoanApplicationFormStore,
} from "@krea/customer-features/store/loan-application-form/hooks";
import { privacyPolicyLink } from "@krea/customer-features/utils";
import CheckBox from "@krea/shared/design-system/fields/Checkbox";
import { CountryCode } from "@krea/shared/types/common";

import CloudLoader from "static/images-new/CloudLoader.gif";

const getValidationSchema = () => {
  return Yup.object().shape({
    organisation: OrganisationValidation(),
    email: EmailValidation(),
    mobilePhoneNumber: MobilePhoneNumberValidation(settings.countryCode),
    consentCheckbox: ConsentCheckboxValidation(),
    requestedLoanAmount: RequestedLoanAmountValidation(),
  });
};

type ValidationSchemaType = Yup.InferType<
  ReturnType<typeof getValidationSchema>
>;

export const PreliminaryBidFormPage = () => {
  const { t, i18n } = useTranslation();
  const [isSubmitting, setSubmitting] = useState(false);
  const [, setStatus] = useState({ success: false });
  const isMobile = useMobile();

  const {
    organisation: { name: organisationName, organisationNumber },
    email,
    mobilePhoneNumber,
    requestedLoanAmount,
    consentCheckbox,
    setOrganisation,
    setEmail,
    setMobilePhoneNumber,
    setRequestedLoanAmount,
    setConsentCheckbox,
    setPreliminaryBid,
    formErrors,
    removeFormError,
    utmCampaign,
  } = useLoanApplicationFormStore(
    useShallow((state) => ({
      organisation: state.organisation,
      email: state.email,
      mobilePhoneNumber: state.mobilePhoneNumber,
      requestedLoanAmount: state.requestedLoanAmount,
      consentCheckbox: state.consentCheckbox,
      setOrganisation: state.setOrganisation,
      setEmail: state.setEmail,
      setMobilePhoneNumber: state.setMobilePhoneNumber,
      setRequestedLoanAmount: state.setRequestedLoanAmount,
      setConsentCheckbox: state.setConsentCheckbox,
      setPreliminaryBid: state.setPreliminaryBid,
      formErrors: state.formErrors,
      removeFormError: state.removeFormError,
      utmCampaign: state.utmCampaign,
    })),
  );

  const { control, handleSubmit, getValues, setValue, setError, formState } =
    useForm<ValidationSchemaType>({
      mode: "onChange",
      defaultValues: {
        organisation: {
          name: organisationName,
          organisationNumber: organisationNumber,
        },
        email,
        mobilePhoneNumber,
        requestedLoanAmount,
      },
      resolver: yupResolver(getValidationSchema()),
    });

  const onSubmit = async () => {
    setSubmitting(true);

    const startTime = Date.now();
    const utmDataJSONString = getUtmDataJSONString(utmCampaign);
    const payload = {
      countryCode: settings.countryCode,
      email,
      mobilePhoneNumber,
      requestedLoanAmount,
      organisationName,
      organisationNumber,
      utmData: utmDataJSONString ? utmDataJSONString : null,
    };

    try {
      const response = await axios.post<IPreliminaryLoanAmountData>(
        `${settings.kreaBaseUrl}/api/v1/preliminary-loan-amount`,
        payload,
      );

      const finishTime = Date.now();
      const timeDiff = finishTime - startTime;
      const conversionValue = response.data.conversionValue;

      let dataLayerData = {};

      if (conversionValue) {
        dataLayerData = {
          event: "LeadGenerated",
          leadGeneratedConversion: {
            conversionValue,
            currency: settings.currency,
          },
        };
      } else {
        dataLayerData = {
          event: "LeadGenerated",
        };
      }

      TagManager.dataLayer({
        dataLayer: {
          ...dataLayerData,
        },
      });

      setStatus({ success: true });

      if (timeDiff >= 3000) {
        setSubmitting(false);
      } else {
        setTimeout(() => {
          setSubmitting(false);
        }, 3000 - timeDiff);
      }

      setOrganisation({
        name: organisationName,
        organisationNumber: organisationNumber,
      });
      setEmail(email);
      setMobilePhoneNumber(parseIncompletePhoneNumber(mobilePhoneNumber));
      setRequestedLoanAmount(
        response.data.useEdgeCaseLogic
          ? requestedLoanAmount
          : response.data.estimatedMaxLoanAmount,
      );
      setPreliminaryBid(response.data);

      router.navigate("/amount-estimator/result");
    } catch (error: unknown) {
      const emailVerificationError = error as IErrorResponse | undefined;

      if (
        emailVerificationError?.data.errors.find(
          (error: ICustomExceptionError) => error.code === "1006",
        )
      ) {
        const translatedErrorMessage = t(
          "applicationForm.commons.emailErrors.validationError",
        );
        setError("email", { message: translatedErrorMessage });
      }
      setStatus({ success: false });
      setSubmitting(false);
    }
  };

  const {
    field: organisationNumberField,
    fieldState: organisationNumberFieldState,
  } = useController({
    name: "organisation.organisationNumber",
    control,
  });

  const { field: emailField, fieldState: emailFieldState } = useController({
    name: "email",
    control,
    defaultValue: "",
  });

  const { field: phoneNumberField, fieldState: phoneNumberFieldState } =
    useController({
      name: "mobilePhoneNumber",
      control,
    });

  const { field: consentCheckboxField, fieldState: consentCheckboxFieldState } =
    useController({
      name: "consentCheckbox",
      control,
      defaultValue: false,
    });

  const {
    field: { value, onChange: onInputChange, onBlur: onInputBlur },
    fieldState,
  } = useController({
    name: "requestedLoanAmount",
    control,
  });

  const onChange = (name: string, value: number) => {
    onInputChange(value);
    setRequestedLoanAmount(value);
  };

  let isLimitedCompany = false;

  if (
    organisationName &&
    !organisationNumberFieldState.error &&
    organisationNumberFieldState.isTouched
  ) {
    const organisationSplitName = organisationName.toLowerCase().split(" ");

    switch (settings.countryCode) {
      case CountryCode.SE:
        isLimitedCompany =
          organisationName.replace(" ", "") !== "ab" &&
          organisationSplitName.includes("ab");
        break;
      case CountryCode.FI:
        isLimitedCompany =
          organisationName.replace(" ", "") !== "oy" &&
          organisationSplitName.includes("oy");
        break;
      default:
        isLimitedCompany = false;
    }
  }

  const infoText = (
    <span>
      {t("applicationForm.common.businessNotFoundContactSupport")}
      {isMobile && <br />}
      {t("applicationForm.common.callUs")}{" "}
      <Button
        variant="text"
        style={{ whiteSpace: "nowrap" }}
        className="tw-font-bold tw-text-primary"
        size={isMobile ? "sm" : "md"}
        onClick={() =>
          window.open(`tel:${settings.contacts.phone.link}`, "_self")
        }
      >
        {settings.contacts.phone.value}
      </Button>
    </span>
  );

  const disableNavigation = !(
    organisationNumberField.value &&
    emailField.value &&
    phoneNumberField.value &&
    requestedLoanAmount &&
    consentCheckboxField.value &&
    isEmpty(organisationNumberFieldState.error) &&
    isEmpty(emailFieldState.error) &&
    isEmpty(phoneNumberFieldState.error) &&
    isEmpty(fieldState.error)
  );

  const triggerFieldValidation = (fields: Path<ValidationSchemaType>[]) => {
    for (const field of fields) {
      const fieldValue = getValues(field);

      if (isObject(fieldValue)) {
        Object.keys(fieldValue).forEach((key) => {
          const nestedField = `${field}.${key}` as Path<ValidationSchemaType>;
          triggerFieldValidation([nestedField]);
        });
      }
      setValue(field, fieldValue, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  };

  const innerForm = (
    <>
      {isSubmitting && (
        <div>
          <div className="tw-mt-7 tw-flex tw-h-3/4 tw-w-full tw-flex-col tw-items-center">
            <div className="tw-m-0 tw-text-center tw-text-[1.5rem]">
              {i18n.t("preliminaryBidForm.loadingText")}
            </div>
            <div className={"tw-inset-0 tw-w-full"}>
              <img
                className="tw-relative tw-mx-auto tw-h-auto tw-w-[200px]"
                src={CloudLoader}
                alt="Loader"
              />
            </div>
          </div>
        </div>
      )}

      {!isSubmitting && (
        <div>
          <div className="tw-mt-2">
            <OrganisationInput
              value={organisationNumberField.value}
              error={
                formState.errors.organisation?.organisationNumber?.message ??
                formState.errors.organisation?.name?.message
              }
              touched={organisationNumberFieldState.isTouched}
              onCompanyChange={(company) => {
                setValue(
                  "organisation.organisationNumber",
                  company.organisationNumber,
                  {
                    shouldTouch: true,
                    shouldDirty: true,
                    shouldValidate: true,
                  },
                );
                setValue("organisation.name", company.name, {
                  shouldTouch: true,
                  shouldDirty: true,
                  shouldValidate: true,
                });

                setOrganisation(company);
              }}
              isLimitedCompany={isLimitedCompany}
              onBlur={() => {
                triggerFieldValidation(["organisation"]);
              }}
            />
          </div>
          <div className="tw-mx-1 tw-mt-7">
            <AmountSlider
              name={"requestedLoanAmount"}
              value={value}
              onInputBlur={onInputBlur}
              setInputValue={onChange}
              errors={fieldState.error?.message}
              touched={fieldState.isTouched}
              isRealEstate={false}
            />
          </div>
          <div className="tw-mt-5">
            <EmailInput
              label={t("applicationForm.commons.emailLabel", {
                ns: "common",
              })}
              value={emailField.value}
              errors={emailFieldState.error?.message}
              touched={formErrors.email || emailFieldState.isTouched}
              handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                emailField.onChange(e.target.value);
                setEmail(e.target.value);
                removeFormError("email");
              }}
              onBlur={() => {
                triggerFieldValidation(["email"]);
              }}
            />
          </div>

          <div className="tw-mb-4 tw-mt-5">
            <PhoneInput
              id="PreliminaryBidFormMobilePhoneNumber"
              label={t("applicationForm.commons.phoneLabel", {
                ns: "common",
              })}
              value={phoneNumberField.value}
              touched={phoneNumberFieldState.isTouched}
              errors={phoneNumberFieldState.error?.message}
              handleChange={(e) => {
                setMobilePhoneNumber(e.target.value);
                phoneNumberField.onChange(e.target.value);
              }}
              onBlur={() => {
                triggerFieldValidation(["mobilePhoneNumber"]);
              }}
            />
          </div>

          <CheckBox
            onChange={() => {
              setConsentCheckbox(!consentCheckbox);
              consentCheckboxField.onChange(!consentCheckboxField.value);
            }}
            checked={consentCheckboxField.value}
            error={consentCheckboxFieldState.error?.message}
            className="tw-my-7"
            label={
              <Trans
                i18nKey={"applicationForm.common.privacyPolicyConsent"}
                values={{
                  company: (() => {
                    switch (settings.countryCode) {
                      case CountryCode.FI:
                        return "Kredita";
                      case CountryCode.SE:
                        return "Krea";
                      default:
                        return "";
                    }
                  })(),
                }}
                components={{
                  1: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a
                      target="_blank"
                      href={privacyPolicyLink(
                        settings.countryCode,
                        settings.countryWebsiteUrl,
                        i18n.language,
                      )}
                      rel="noopener noreferrer"
                      className="tw-font-bold tw-text-primary tw-no-underline"
                    />
                  ),
                }}
              />
            }
            labelClassName="tw-font-normal tw-text-sm"
          />
        </div>
      )}
    </>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="tw-flex">
        <div style={isMobile ? { width: "100%" } : { width: "65%" }}>
          <div className="tw-mx-auto" style={!isMobile ? { width: "80%" } : {}}>
            <LoanApplicationFormLayout
              heading={t("preliminaryBidForm.preliminaryBidHeading")}
              subheading={
                <>
                  <div className="tw-inline-block">
                    <div className="tw-flex tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-mint-500 tw-bg-mint-100 tw-px-3 tw-py-1">
                      <div className="tw-flex tw-h-[15px] tw-w-[15px] tw-items-center tw-justify-center tw-rounded-full tw-bg-mint-500">
                        <SVG src={Check} className="tw-h-auto tw-w-[9px]" />
                      </div>
                      <Text size="sm" className="tw-ml-[8px]">
                        {settings.countryCode === CountryCode.SE
                          ? t(
                              "preliminaryBidForm.immediateResponseWithoutCreditCheck",
                            )
                          : t("preliminaryBidForm.immediateResponse")}
                      </Text>
                    </div>
                  </div>
                </>
              }
              innerForm={innerForm}
              bottomInfoText={isSubmitting ? null : infoText}
              hideAdvisorCTABanner
              hasTestimonials
              hideNavigation={isSubmitting}
              disabledNavigationBeforeFormCompletion={disableNavigation}
              customButtonText={
                settings.countryCode === CountryCode.FI
                  ? t("preliminaryBidForm.estimateLimitFinnishVersion")
                  : t("preliminaryBidForm.estimateLimit")
              }
              borderlessInnerForm
              goToNextStep={() => {
                // Ensures validation is triggered when an incorrect value has been cached and set as the default value of an input field
                triggerFieldValidation([
                  "organisation",
                  "email",
                  "mobilePhoneNumber",
                  "requestedLoanAmount",
                ]);
              }}
              goToPreviousStep={() => {}}
            />
          </div>
        </div>

        {!isMobile && (
          <div
            className="tw-mx-2"
            style={{
              minWidth: "400px",
              maxWidth: "500px",
              minHeight: "855px",
              maxHeight: "950px",
            }}
          >
            <AppFormPromotion />
          </div>
        )}
      </div>
    </form>
  );
};
